import React, { useState, useEffect } from "react";
import NumberInput from "@/components/elements/NumberInput";
import Label from "@/components/elements/Label";
import { calculateSagitta, calculateSquareDiagonal, calculateDistance } from "@/services/geometry.js";

/**
 * CutOffInputList component for selecting cutoff types
 *
 * @param {Object} props - Component props
 * @param {Object} props.properties - Current cutoff properties
 * @param {Function} props.handlePropertyUpdate - Function to update individual properties
 * @param {Object} props.plateDimensions - Plate dimensions
 * @param {Function} props.onFocusedRadiusFieldChange - Callback when focused radius field changes
 * @param {string} props.className - Additional CSS classes
 */
const CutOffInputList = ({
    properties = {},
    handlePropertyUpdate,
    plateDimensions,
    onFocusedRadiusFieldChange
    
}) => {
    const [focusedRadiusField, setFocusedRadiusField] = useState(null);
    const [isRound, setIsRound] = useState(properties.is_round || false);


    // Sync isRound state with properties (but avoid infinite loops)
    useEffect(() => {
        setIsRound(properties.is_round || false);
    }, [properties.is_round]);
    
    /**
     * Calculate minimum radius for a cutoff line to ensure valid sagitta
     * @param {string} lineType - Either 'right' or 'left'
     * @returns {number} Minimum radius in mm
     */
    const calculateMinRadius = (lineType) => {
        if (!properties.pos_tip_left || !plateDimensions) {
            return 0;
        }
        
        // Define tip point (always the same)
        const tipX = properties.pos_tip_left;
        const tipY = 0;
        
        // Define end point based on line type
        let endX, endY;
        if (lineType === 'right') {
            if (!properties.pos_right_bottom) return 0;
            endX = plateDimensions.width;
            endY = plateDimensions.height - properties.pos_right_bottom;
        } else if (lineType === 'left') {
            if (!properties.pos_left_bottom) return 0;
            endX = 0;
            endY = plateDimensions.height - properties.pos_left_bottom;
        } else {
            return 0;
        }
        
        const chordLength = calculateDistance(tipX, tipY, endX, endY);
        
        // Minimum radius is half the chord length + 1 to ensure sagitta > 0
        return Math.ceil(chordLength / 2) + 1;
    };

    /**
     * Handle focus on radius fields
     * @param {string} fieldName - Name of the radius field being focused
     */
    const handleRadiusFocus = (fieldName) => {
        setFocusedRadiusField(fieldName);
        // Notify parent component
        if (onFocusedRadiusFieldChange) {
            onFocusedRadiusFieldChange(fieldName);
        }
    };

    /**
     * Handle blur on radius fields
     */
    const handleRadiusBlur = () => {
        setFocusedRadiusField(null);
        // Notify parent component
        if (onFocusedRadiusFieldChange) {
            onFocusedRadiusFieldChange(null);
        }
    };

    /**
     * Handle is round toggle change
     * @param {boolean} value - New is round value
     */
    const handleIsRoundChange = (value) => {
        
        if (!handlePropertyUpdate) return;
        
        // Update local state immediately for responsive UI
        setIsRound(value);
        
        // If enabling round mode, calculate and set symmetric values
        if (value && plateDimensions) {
            // Set tip in the middle
            const middlePosition = plateDimensions.width / 2;
            
            // Calculate symmetric distance from edges (25% from each edge by default)
            const symmetricHeightDistance = plateDimensions.height - middlePosition;

            // Get the chord length of the circle
            const chordLength = calculateSquareDiagonal(middlePosition, middlePosition);
            
            // Update all properties at once - the parent now uses ...prev pattern
            handlePropertyUpdate("is_round")({
                is_round: value,
                pos_tip_left: middlePosition,
                pos_left_bottom: symmetricHeightDistance,
                pos_right_bottom: symmetricHeightDistance,
                curve_left: Math.round(middlePosition),
                curve_right: Math.round(middlePosition)
            });
        } else {
            // Just update the is_round property when disabling
            handlePropertyUpdate("is_round")({ is_round: value });
        }
    };

    return (
        <div className={`cutoffoptions`}>
            <div className="row mb-3">
                <div className="col-12">
                    <div className="form-check">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="isRoundToggle"
                            checked={isRound}
                            onChange={(e) => handleIsRoundChange(e.target.checked)}
                        />
                        <label 
                            className="form-check-label" 
                            htmlFor="isRoundToggle"
                            style={{ fontSize: 14, marginLeft: 8, fontWeight: "500" }}
                        >
                            Is rond
                        </label>
                    </div>
                </div>
            </div>

            <Label label="Afnijding vanaf links" small />
            <NumberInput
                properties={properties}
                property="pos_left_bottom"
                field={{ min: 0, max: plateDimensions.height, unit: "mm", disabled: isRound }}
                updateProperties={handlePropertyUpdate ? handlePropertyUpdate("pos_left_bottom") : () => {}}
            />
            <Label label="Afnijding vanaf boven" small />
            <NumberInput
                properties={properties}
                property="pos_tip_left"
                field={{ min: 1, max: plateDimensions.width, unit: "mm", disabled: isRound }}
                updateProperties={handlePropertyUpdate ? handlePropertyUpdate("pos_tip_left") : () => {}}
            />
            <Label label="Afnijding vanaf rechts" small />
            <NumberInput
                properties={properties}
                property="pos_right_bottom"
                field={{ min: 1, max: plateDimensions.height, unit: "mm", disabled: isRound }}
                updateProperties={handlePropertyUpdate ? handlePropertyUpdate("pos_right_bottom") : () => {}}
            />
                {properties.pos_left_bottom > 0 && (
                <>
                    <Label label="radius linker lijn" small />
                    <NumberInput
                        properties={properties}
                        property="curve_left"
                        field={{ min: calculateMinRadius('left'), unit: "mm", disabled: isRound }}
                        updateProperties={handlePropertyUpdate ? handlePropertyUpdate("curve_left") : () => {}}
                        onFocus={() => handleRadiusFocus("curve_left")}
                        onBlur={handleRadiusBlur}
                    />
                </>
            )}
            {properties.pos_right_bottom > 0 && (
                <>
                    <Label label="radius rechter lijn" small />
                    <NumberInput
                        properties={properties}
                        property="curve_right"
                        field={{ min: calculateMinRadius('right'), unit: "mm", disabled: isRound }}
                        updateProperties={handlePropertyUpdate ? handlePropertyUpdate("curve_right") : () => {}}
                        onFocus={() => handleRadiusFocus("curve_right")}
                        onBlur={handleRadiusBlur}
                    />
                </>
            )}
        </div>
    );
};

export default CutOffInputList; 